// F15a region capture (FR-RD1): pick a region on a still of the chosen display, then capture
// it natively (`region:x,y,w,h`, cropped by capture/region.rs + the ffmpeg grab). The picker is
// an in-window dialog over the display preview rather than a per-monitor transparent overlay
// window; it keeps the same contract (drag, dimmed outside, live size, Enter/Esc, last region).
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { Button } from "@grackle/ui";
import type { NativeCaptureSource } from "../desktop";
import { MIN_REGION_SIDE, type Rect } from "./regionCrop";

const LAST_REGION_KEY = "grackle.recorder.lastRegion";

export function loadLastRegion(): Rect | null {
  try {
    const raw = localStorage.getItem(LAST_REGION_KEY);
    const r = raw ? (JSON.parse(raw) as Rect) : null;
    return r && r.width >= MIN_REGION_SIDE && r.height >= MIN_REGION_SIDE ? r : null;
  } catch {
    return null;
  }
}

export function saveLastRegion(r: Rect): void {
  try {
    localStorage.setItem(LAST_REGION_KEY, JSON.stringify(r));
  } catch {
    // Storage may be unavailable; the region still applies to this recording.
  }
}

/** Selection in preview (client) pixels -> display coordinates, rounded to even sizes. */
export function previewToDisplay(sel: Rect, previewWidth: number, previewHeight: number, display: NativeCaptureSource): Rect {
  const sx = display.width / previewWidth;
  const sy = display.height / previewHeight;
  const even = (v: number) => Math.max(2, Math.floor(v) & ~1);
  return {
    x: Math.round(display.x + sel.x * sx),
    y: Math.round(display.y + sel.y * sy),
    width: even(sel.width * sx),
    height: even(sel.height * sy),
  };
}

function normalize(a: { x: number; y: number }, b: { x: number; y: number }): Rect {
  return { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), width: Math.abs(a.x - b.x), height: Math.abs(a.y - b.y) };
}

export interface RegionPickerProps {
  display: NativeCaptureSource;
  /** data: URL of the display's still (native_preview_jpeg). */
  previewUrl: string;
  onPick: (region: Rect) => void;
  onCancel: () => void;
}

export default function RegionPicker({ display, previewUrl, onPick, onCancel }: RegionPickerProps) {
  const frameRef = useRef<HTMLDivElement>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const [sel, setSel] = useState<Rect | null>(null);

  useEffect(() => {
    frameRef.current?.focus();
  }, []);

  // Restore the last region (display coordinates) once the preview has a size.
  function onImageLoad() {
    const last = loadLastRegion();
    const box = frameRef.current?.getBoundingClientRect();
    if (!last || !box || box.width === 0) return;
    const sx = box.width / display.width;
    const sy = box.height / display.height;
    setSel({ x: (last.x - display.x) * sx, y: (last.y - display.y) * sy, width: last.width * sx, height: last.height * sy });
  }

  function point(e: PointerEvent<HTMLDivElement>) {
    const box = e.currentTarget.getBoundingClientRect();
    return {
      x: Math.min(Math.max(0, e.clientX - box.left), box.width),
      y: Math.min(Math.max(0, e.clientY - box.top), box.height),
    };
  }

  const box = frameRef.current?.getBoundingClientRect();
  const region = sel && box && box.width > 0 ? previewToDisplay(sel, box.width, box.height, display) : null;
  const valid = Boolean(region && region.width >= MIN_REGION_SIDE && region.height >= MIN_REGION_SIDE);

  function confirm() {
    if (!region || !valid) return;
    saveLastRegion(region);
    onPick(region);
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    } else if (e.key === "Enter") {
      e.preventDefault();
      confirm();
    }
  }

  return (
    <div className="region-picker" role="dialog" aria-label="Select a region to record" onKeyDown={onKeyDown}>
      <div
        ref={frameRef}
        tabIndex={0}
        className="region-picker__frame"
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture?.(e.pointerId);
          start.current = point(e);
          setSel(null);
        }}
        onPointerMove={(e) => {
          if (start.current) setSel(normalize(start.current, point(e)));
        }}
        onPointerUp={() => {
          start.current = null;
        }}
      >
        <img src={previewUrl} alt={`Preview of ${display.label}`} draggable={false} onLoad={onImageLoad} />
        {sel ? (
          <div
            className="region-picker__sel"
            style={{ left: sel.x, top: sel.y, width: sel.width, height: sel.height }}
            data-testid="region-selection"
          />
        ) : (
          <div className="region-picker__dim" />
        )}
      </div>
      <div className="region-picker__bar">
        <span className="mono" aria-live="polite">
          {region ? `${region.width} × ${region.height}` : "Drag to select a region"}
        </span>
        <span className="region-picker__spacer" />
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={confirm} disabled={!valid}>
          Record this region
        </Button>
      </div>
    </div>
  );
}
